<template lang="pug">
AppSelectListItem.activities-location-sidebar-member-item(
  :class="{ active: selected }"
  @click="$emit('click')"
)
  template(#label)
    .member-item-wrapper
      .member-item-section
        .custom-select-list-item-avatar(:class="{ active: selected }")
          PopoverAvatar(
            :user-id="user.id"
            :organization-id="organizationId"
            avatar-style="thumb"
          )
          span(
            v-if="!isPast"
            :class="workingClass"
          )
        .custom-select-list-item-label
          span {{ user.name }}
          span.member-project.hint.is-regular
            | {{ projectTaskText }}
          template(v-if="!isPast")
            span.member-address.hint.is-regular(v-if="user.trackedLocation && currentAddress")
              | {{ currentAddress }}
            span.member-since.hint.is-regular(v-if="user.trackedLocation && locationAtSince")
              | {{ locationAtSince }}
        .custom-select-list-item-duration
          template(v-if="user.trackedLocation") {{ duration }}
          template(v-else) -
      .member-item-section.on-selected.mt-10(v-if="selected")
        template(v-if="user.trackedLocation")
          ActivitiesLocationViewButton.view-info-btn.mr-15(
            :active="activeInfo"
            text="info"
            icon="hi-group-by-rounded"
            @click="onInfoClick"
          )
          ActivitiesLocationViewButton.view-route-btn(
            :active="activeRoute"
            text="routes"
            icon="hi-route"
            @click="onRouteClick"
          )
        .d-flex.flex-column(v-else)
          p.hint.small.m-0 No locations for this day.
          p.hint.small.m-0 Most recent:&nbsp;
            a.btn.btn-naked.p-0(@click.prevent="$emit('change-date', lastLocationRecordedAtInternalDate)")
              | {{ mostRecentDateLabel }}
</template>

<script>
import { get } from 'lodash-es';
import moment from 'moment';
import { mapGetters } from 'vuex';
import { formatDuration, formatInternalDate } from '~/utils/time_utils';
import { WorkingProjectTask } from '~/mixins';
import PopoverAvatar from '~/components/PopoverAvatar.vue';

export default {
  name: 'ActivitiesLocationSidebarMemberItem',
  components: {
    PopoverAvatar,
  },
  mixins: [WorkingProjectTask],
  props: {
    user: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    activeInfo: {
      type: Boolean,
      default: false,
    },
    activeRoute: {
      type: Boolean,
      default: false,
    },
    isPast: Boolean,
    currentAddress: {
      type: String,
      default: null,
    },
    addressTimestamp: {
      type: Object,
      default: null,
    },
  },
  emits: ['click', 'change-date', 'hide-info', 'view-info', 'hide-route', 'view-route'],
  computed: {
    ...mapGetters(['activeTimeZoneName', 'organizationId']),
    workingClass() {
      return {
        status: true,
        'status-active': this.user.activeNow,
      };
    },
    projectTaskText() {
      return `${this.workingProjectForUser(this.user)} / ${this.workingTaskForUser(this.user)}`;
    },
    duration() {
      const duration = get(this.user, 'lastActivity.total_tracked');

      return duration ? formatDuration(duration, { showSeconds: 'never' }) : '-';
    },
    // Only change the `locationAtSince` whenever the `current-address` prop changes.
    // Otherwise we could be updating it based on the "last location", which is not the last location that we geocoded
    // in the parent component `ActivitiesLocationSidebarMembersTab`.
    locationAtSince() {
      if (!this.user.trackedLocation || !this.currentAddress) {
        return null;
      }

      // Use the `addressTimestamp` if present (the address was geocoded), otherwise
      // use the last known address; which will be a jobsite since we didn't
      // specifically set the timestamp.
      //
      // `addressTimestamp` is already coerced to the timezone if present.
      const recordedAt = this.addressTimestamp ? this.addressTimestamp : this.lastLocationRecordedAt;
      const now = this.activeTimeZoneName ? moment.tz(moment(), this.activeTimeZoneName) : moment();

      return `Since ${recordedAt.from(now, true)}`;
    },

    // We don't need to change the below computed properties for the `addressTimestamp` since
    // this are only used when the user is on the bottom group (it didn't track location) or
    // when we're in past view.
    lastLocationRecordedAt() {
      const locationRecordedAt = get(this.user, 'location.recorded_at');

      if (!locationRecordedAt) {
        return null;
      }

      return this.activeTimeZoneName
        ? moment.tz(locationRecordedAt, this.activeTimeZoneName)
        : moment(locationRecordedAt);
    },
    lastLocationRecordedAtInternalDate() {
      return this.lastLocationRecordedAt ? formatInternalDate(this.lastLocationRecordedAt) : null;
    },
    mostRecentDateLabel() {
      if (this.user.trackedLocation || !this.lastLocationRecordedAt) {
        return null;
      }

      return this.lastLocationRecordedAt.format('MMM D, YYYY');
    },
  },
  methods: {
    onInfoClick() {
      const event = this.activeInfo ? 'hide-info' : 'view-info';

      this.$emit(event);
    },
    onRouteClick() {
      const event = this.activeRoute ? 'hide-route' : 'view-route';

      this.$emit(event);
    },
  },
};
</script>
